/**
 * The suite's cookie-consent banner.
 *
 * Served by lithos-idp so that one choice covers every Lithos service; the API is
 * copied from its `docs/20-cookie-consent.md`. Injected rather than written into
 * `index.html` because the realm's origin is deployment configuration, which
 * reaches the page only through `/config.js`.
 */
import { useEffect, useState } from 'react'

import { getRuntimeConfig } from '@/shared/config/runtime'

export type ConsentCategory = 'analytics' | 'diagnostics'

export interface LithosConsent {
  /** Stamps the record with the signed-in person. A different person on this browser is asked again. */
  identify(email: string): Promise<void>
  /** Whether every cookie in the category is granted. False until the widget is ready. */
  granted(category: ConsentCategory): boolean
  /** Calls `listener` with the current answer once ready, then on every change. Returns an unsubscribe. */
  onChange(category: ConsentCategory, listener: (granted: boolean) => void): () => void
  /** Opens the preferences dialog, for a "Cookie settings" link. */
  openPreferences(): void
}

declare global {
  interface Window {
    lithosConsent?: LithosConsent
  }
}

let loading: Promise<LithosConsent | null> | null = null

/**
 * Loads the banner once. Null with no realm configured, or when the script fails
 * to load.
 */
export function loadLithosConsent(): Promise<LithosConsent | null> {
  if (loading === null) {
    const issuer = getRuntimeConfig().idpIssuer
    loading =
      issuer === ''
        ? Promise.resolve(null)
        : new Promise((resolve) => {
            const script = document.createElement('script')
            script.src = `${new URL(issuer).origin}/consent/v1/consent.js`
            script.onload = () => resolve(window.lithosConsent ?? null)
            script.onerror = () => resolve(null)
            document.head.appendChild(script)
          })
  }
  return loading
}

/** The banner, once it has loaded. */
export function useLithosConsent(): LithosConsent | null {
  const [consent, setConsent] = useState<LithosConsent | null>(null)
  useEffect(() => {
    let live = true
    void loadLithosConsent().then((loaded) => {
      if (live) setConsent(loaded)
    })
    return () => {
      live = false
    }
  }, [])
  return consent
}

/** Test seam: forgets the loaded banner. */
export function __resetLithosConsentForTests(): void {
  loading = null
}
